import { useEffect, useState } from "react";
import { Download, Bell, X } from "lucide-react";
import { toast } from "sonner";
import { canInstall, promptInstall, isStandalone } from "@/lib/pwaInstall";

export default function InstallPrompt() {
  const alreadyInstalled = () =>
    localStorage.getItem("rca_app_installed") === "1" || isStandalone();
  const [dismissed, setDismissed] = useState(
    () => alreadyInstalled() || localStorage.getItem("rca_install_dismissed") === "1"
  );
  // Re-render when the browser's install prompt becomes available (captured early in
  // lib/pwaInstall at app entry, so it isn't missed before this banner mounts).
  const [, force] = useState(0);

  useEffect(() => {
    const rerender = () => force((n) => n + 1);
    const onInstalled = () => { localStorage.setItem("rca_app_installed", "1"); setDismissed(true); };
    window.addEventListener("rca:installable", rerender);
    window.addEventListener("rca:installed", onInstalled);
    return () => {
      window.removeEventListener("rca:installable", rerender);
      window.removeEventListener("rca:installed", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    const outcome = await promptInstall();
    if (outcome === "accepted") { setDismissed(true); return; }
    if (outcome === "unavailable") {
      toast("To install", {
        description:
          "iPhone/iPad: tap Share, then \u201CAdd to Home Screen.\u201D Android/desktop: open the browser menu, then \u201CInstall app.\u201D",
        duration: 8000,
      });
    }
  };

  const handleNotifications = async () => {
    if (!("Notification" in window)) {
      toast.error("This device does not support notifications.");
      return;
    }
    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      toast.success("Notifications are on.", {
        description: "You'll get reminders here once Rosemoor starts sending them.",
      });
    } else {
      toast("Notifications are off.", { description: "You can turn them on any time." });
    }
  };

  if (dismissed) return null;

  return (
    <div
      data-testid="install-prompt-bar"
      className="flex items-center gap-4 px-5 py-3 bg-gold text-ink flex-wrap"
    >
      <Download className="w-5 h-5 shrink-0" />
      <div className="flex-1 min-w-[220px]">
        <div className="font-bold text-base">Install Rosemoor on this device</div>
        <div className="text-[15px] leading-snug text-ink/85">
          Opens full screen without a browser bar, and lets us send you reminders. Works the same on a phone, tablet, or computer.
        </div>
      </div>
      <button
        data-testid="install-app-button"
        onClick={handleInstall}
        className="bg-ink text-cream font-bold rounded-lg px-5 py-3 min-h-[46px] text-[15px] transition-transform hover:-translate-y-0.5"
      >
        Install app
      </button>
      <button
        data-testid="enable-notifications-button"
        onClick={handleNotifications}
        className="border-[1.5px] border-ink/40 rounded-lg px-4 py-3 min-h-[46px] text-[15px] font-semibold flex items-center gap-2 transition-colors hover:bg-ink/10"
      >
        <Bell className="w-4 h-4" /> Turn on notifications
      </button>
      <button
        data-testid="dismiss-install-button"
        onClick={() => { localStorage.setItem("rca_install_dismissed", "1"); setDismissed(true); }}
        aria-label="Dismiss"
        className="text-ink/55 text-2xl leading-none px-2 hover:text-ink"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}
